// ─────────────────────────────────────────────────────────────────────────────
// Archivo: reportesRTIService.js
// Ubicación sugerida: src/services/reportesRTIService.js
//
// Reemplaza toda la lógica de useMemo del componente ReportesTramitesRTI.
// Cada función llama a un endpoint distinto y devuelve los datos
// ya calculados por el backend.
//
// Uso en el componente:
//
//   import * as reportesRTIService from '../../services/reportesRTIService';
//
//   // Carga inicial (todos en paralelo)
//   const data = await reportesRTIService.getAllReportes(filtros);
//
//   // O individualmente al cambiar filtros
//   const porEstado = await reportesRTIService.getPorEstado(filtros);
// ─────────────────────────────────────────────────────────────────────────────

import apiClient from '../apiClient';

const BASE_URL = '/reportes/rti';

// ── Helper: construir params a partir del objeto de filtros ───────────────────
/**
 * @param {Object} filtros
 * @param {string|null} filtros.fechaDesde  — "yyyy-MM-dd" o null
 * @param {string|null} filtros.fechaHasta  — "yyyy-MM-dd" o null
 * @returns {URLSearchParams}
 */
function buildParams(filtros = {}) {
    const params = new URLSearchParams();
    if (filtros.fechaDesde) params.append('fechaDesde', filtros.fechaDesde);
    if (filtros.fechaHasta) params.append('fechaHasta', filtros.fechaHasta);
    return params;
}

// ── 1. Resumen general (KPIs del header) ──────────────────────────────────────
/**
 * GET /reportes/rti/resumen
 *
 * Devuelve los KPIs para los tres cards superiores del componente.
 *
 * @param {Object} filtros
 * @returns {Promise<{
 *   totalTramites: number,
 *   estadosActivos: number,
 *   estadoMasFrecuenteNombre: string|null,
 *   estadoMasFrecuenteCantidad: number|null
 * }>}
 *
 * Uso en el componente (reemplaza estos useMemo/useState):
 *
 *   // ANTES
 *   const total = tramitesFiltrados.length;
 *   const estadosActivos = Object.keys(agrupacionPorEstado).length;
 *   const estadoTop = Object.entries(agrupacionPorEstado).sort(...)[0];
 *
 *   // DESPUÉS
 *   const resumen = await getResumen(filtros);
 *   // resumen.totalTramites, resumen.estadosActivos, resumen.estadoMasFrecuenteNombre
 */
export async function getResumen(filtros = {}) {
    const { data } = await apiClient.get(`${BASE_URL}/resumen`, {
        params: buildParams(filtros),
    });
    return data;
}

// ── 2. Distribución / Cantidad por estado ─────────────────────────────────────
/**
 * GET /reportes/rti/por-estado
 *
 * Un ítem por cada estado con trámites RTI.
 * Sirve para el gráfico de TORTA (proporción) y el de BARRAS (cantidad).
 * Ordenado por cantidad DESC.
 *
 * @param {Object} filtros
 * @returns {Promise<Array<{
 *   idEstado: number,
 *   nombreEstado: string,
 *   descripcionEstado: string|null,
 *   cantidad: number,
 *   porcentaje: number
 * }>>}
 *
 * Uso en el componente (reemplaza agrupacionPorEstado + datosPie + datosBar):
 *
 *   // ANTES — useMemo que recorre tramitesFiltrados
 *   const agrupacionPorEstado = useMemo(() => {
 *       const counts = {};
 *       tramitesFiltrados.forEach(t => { ... });
 *       return counts;
 *   }, [...]);
 *
 *   // DESPUÉS — datos ya agrupados
 *   const porEstado = await getPorEstado(filtros);
 *
 *   // Construir datos para Chart.js (torta):
 *   const datosPie = {
 *       labels: porEstado.map(e => e.nombreEstado),
 *       datasets: [{ data: porEstado.map(e => e.cantidad), ... }]
 *   };
 *
 *   // Construir datos para Chart.js (barras):
 *   const datosBar = {
 *       labels: porEstado.map(e => e.nombreEstado),
 *       datasets: [{ data: porEstado.map(e => e.cantidad), ... }]
 *   };
 */
export async function getPorEstado(filtros = {}) {
    const { data } = await apiClient.get(`${BASE_URL}/por-estado`, {
        params: buildParams(filtros),
    });
    return data;
}

// ── 3. Tiempo promedio por estado ─────────────────────────────────────────────
/**
 * GET /reportes/rti/tiempo-promedio
 *
 * Días promedio por estado, calculado desde el historial de cambios.
 * Incluye clasificación automática del backend: "Rápido" | "Normal" | "Requiere atención".
 * Ordenado por diasPromedio DESC.
 *
 * @param {Object} filtros
 * @returns {Promise<Array<{
 *   idEstado: number,
 *   nombreEstado: string,
 *   diasPromedio: number,
 *   cantidadMuestras: number,
 *   clasificacion: "Rápido" | "Normal" | "Requiere atención"
 * }>>}
 *
 * Uso en el componente (reemplaza tiempoPromedioPorEstado + datosBarTiempo):
 *
 *   // ANTES — useMemo que recorre historiales con lógica compleja
 *   const tiempoPromedioPorEstado = useMemo(() => {
 *       const tiemposPorEstado = {};
 *       tramitesFiltrados.forEach(tramite => {
 *           const historial = historiales[tramite.idTramite] || [];
 *           // ... 40 líneas de lógica
 *       });
 *       return promedios;
 *   }, [...]);
 *
 *   // DESPUÉS — datos ya calculados
 *   const tiempoPromedio = await getTiempoPromedio(filtros);
 *
 *   // Construir datos para Chart.js (barras horizontales):
 *   const datosBarTiempo = {
 *       labels: tiempoPromedio.map(e => e.nombreEstado),
 *       datasets: [{
 *           data: tiempoPromedio.map(e => e.diasPromedio),
 *           ...
 *       }]
 *   };
 *
 *   // Tabla detalle: la clasificación ya viene del backend
 *   // → badge rojo si "Requiere atención", verde si "Rápido"
 */
export async function getTiempoPromedio(filtros = {}) {
    const { data } = await apiClient.get(`${BASE_URL}/tiempo-promedio`, {
        params: buildParams(filtros),
    });
    return data;
}

// ── Helper: llamar todos los endpoints en paralelo ────────────────────────────
/**
 * Llama a los 3 endpoints en paralelo con Promise.all.
 * Recomendado para la carga inicial del componente y cuando
 * cambia cualquier filtro (para mantener todos los gráficos sincronizados).
 *
 * @param {Object} filtros
 * @returns {Promise<{
 *   resumen: Object,
 *   porEstado: Array,
 *   tiempoPromedio: Array
 * }>}
 *
 * Uso típico en el componente:
 *
 *   useEffect(() => {
 *       const cargar = async () => {
 *           setLoading(true);
 *           try {
 *               const data = await getAllReportes({
 *                   fechaDesde: filtroFechaInicio || null,
 *                   fechaHasta: filtroFechaFin    || null,
 *               });
 *               setResumen(data.resumen);
 *               setPorEstado(data.porEstado);
 *               setTiempoPromedio(data.tiempoPromedio);
 *           } catch (err) {
 *               console.error('Error cargando reportes RTI:', err);
 *           } finally {
 *               setLoading(false);
 *           }
 *       };
 *       cargar();
 *   }, [filtroFechaInicio, filtroFechaFin]);
 */
export async function getAllReportes(filtros = {}) {
    const [resumen, porEstado, tiempoPromedio] = await Promise.all([
        getResumen(filtros),
        getPorEstado(filtros),
        getTiempoPromedio(filtros),
    ]);
    return { resumen, porEstado, tiempoPromedio };
}